import { router, useForm } from "@inertiajs/react"
import { useEffect, useMemo, useState } from "react"
import { Loader2, Mail, Search, UserPlus } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { cn } from "@/lib/utils"

// Shared "Add Member" modal used from Group/Show, Users index, and any
// future coach page. Two modes:
//   - "existing" → adds an account that's already in any of the coach's
//     groups, via POST /app/coach/group/members
//   - "invite"   → creates an account (or reuses an existing one) and
//     emails a magic-link, via POST /app/coach/group/invites
//
// Group selection: when the coach coaches multiple groups a Select renders
// at the top so they can pick the destination. With a single coached group
// the Select is hidden and the only group is auto-selected. Both server
// endpoints accept an optional `group_id` and validate ownership.
export function AddMemberDialog({
  open,
  onOpenChange,
  searchPool = [],
  groups = [],
  defaultGroupId = null,
}) {
  const [mode, setMode] = useState("existing")
  const [search, setSearch] = useState("")
  const [submitting, setSubmitting] = useState(false)
  const [groupId, setGroupId] = useState(() =>
    defaultGroupId ? String(defaultGroupId) : groups[0]?.id ? String(groups[0].id) : ""
  )

  const inviteForm = useForm({ name: "", email: "" })

  // Re-sync the selected group whenever the dialog reopens with a different
  // default. The user might switch contexts (active group switcher) between
  // openings.
  useEffect(() => {
    if (open) {
      const next = defaultGroupId ?? groups[0]?.id ?? ""
      setGroupId(next ? String(next) : "")
      setMode("existing")
      setSearch("")
      inviteForm.reset()
      inviteForm.clearErrors()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, defaultGroupId])

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return searchPool.slice(0, 50)
    return searchPool
      .filter(
        (p) =>
          p.name?.toLowerCase().includes(q) ||
          p.email?.toLowerCase().includes(q)
      )
      .slice(0, 50)
  }, [search, searchPool])

  const targetGroupName =
    groups.find((g) => String(g.id) === String(groupId))?.name ||
    "your group"

  const handleAddExisting = (accountId) => {
    setSubmitting(true)
    router.post(
      "/app/coach/group/members",
      { account_id: accountId, group_id: groupId },
      {
        preserveScroll: true,
        onFinish: () => {
          setSubmitting(false)
          onOpenChange(false)
        },
      }
    )
  }

  const handleInvite = (e) => {
    e.preventDefault()
    inviteForm.transform((data) => ({ ...data, group_id: groupId }))
    inviteForm.post("/app/coach/group/invites", {
      preserveScroll: true,
      onSuccess: () => {
        inviteForm.reset()
        onOpenChange(false)
      },
    })
  }

  const showGroupPicker = groups.length > 1

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>
            {showGroupPicker ? "Add a member" : `Add a member to ${targetGroupName}`}
          </DialogTitle>
          <DialogDescription>
            Pick someone already in one of your groups, or invite a new person
            by email.
          </DialogDescription>
        </DialogHeader>

        {showGroupPicker && (
          <div className="space-y-1.5">
            <Label>Group</Label>
            <Select value={groupId} onValueChange={setGroupId}>
              <SelectTrigger>
                <SelectValue placeholder="Select a group" />
              </SelectTrigger>
              <SelectContent>
                {groups.map((g) => (
                  <SelectItem key={g.id} value={String(g.id)}>
                    {g.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        <div className="flex gap-1 border-b border-zinc-200">
          {[
            { key: "existing", label: "From your groups", count: searchPool.length },
            { key: "invite", label: "Invite new", count: null },
          ].map((tab) => (
            <button
              key={tab.key}
              onClick={() => setMode(tab.key)}
              className={cn(
                "px-4 py-2 text-sm font-medium border-b-2 transition-colors",
                mode === tab.key
                  ? "border-kf-red text-kf-red"
                  : "border-transparent text-zinc-500 hover:text-zinc-700"
              )}
            >
              {tab.label}
              {tab.count !== null && (
                <span className="ml-1.5 text-xs text-zinc-400">({tab.count})</span>
              )}
            </button>
          ))}
        </div>

        {mode === "existing" && (
          <div className="space-y-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-400" />
              <Input
                placeholder="Search by name or email..."
                className="pl-9"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                autoFocus
              />
            </div>

            <div className="max-h-80 overflow-y-auto border border-zinc-200 rounded-md">
              {filtered.length === 0 ? (
                <p className="text-center py-8 text-sm text-zinc-500">
                  {searchPool.length === 0
                    ? "No candidates available. Invite a new person instead."
                    : "No matches."}
                </p>
              ) : (
                <ul className="divide-y divide-zinc-100">
                  {filtered.map((person) => (
                    <li
                      key={person.id}
                      className="flex items-center justify-between px-3 py-2 hover:bg-zinc-50"
                    >
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-zinc-900 truncate">
                          {person.name || person.email}
                        </p>
                        {person.name && (
                          <p className="text-xs text-zinc-500 truncate">{person.email}</p>
                        )}
                      </div>
                      <Button
                        size="sm"
                        variant="gradient"
                        onClick={() => handleAddExisting(person.id)}
                        disabled={submitting || !groupId}
                      >
                        Add
                      </Button>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {searchPool.length > 50 && !search && (
              <p className="text-xs text-zinc-500">
                Showing first 50. Use search to narrow down.
              </p>
            )}
          </div>
        )}

        {mode === "invite" && (
          <form onSubmit={handleInvite} className="space-y-4">
            <div className="space-y-1.5">
              <Label>Name</Label>
              <Input
                value={inviteForm.data.name}
                onChange={(e) => inviteForm.setData("name", e.target.value)}
                placeholder="Jane Doe"
              />
            </div>

            <div className="space-y-1.5">
              <Label>Email</Label>
              <Input
                type="email"
                required
                value={inviteForm.data.email}
                onChange={(e) => inviteForm.setData("email", e.target.value)}
                placeholder="jane@example.com"
              />
              {inviteForm.errors.email && (
                <p className="text-xs text-red-600">{inviteForm.errors.email}</p>
              )}
            </div>

            <p className="flex items-start gap-1.5 text-xs text-zinc-500">
              <Mail className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
              <span className="min-w-0">
                A sign-in link will be emailed to them. They'll be added to{" "}
                <span className="font-medium">{targetGroupName}</span> automatically.
              </span>
            </p>

            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button
                type="submit"
                variant="gradient"
                disabled={inviteForm.processing || !groupId}
              >
                {inviteForm.processing ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-1.5" />
                ) : (
                  <UserPlus className="h-4 w-4 mr-1.5" />
                )}
                Send Invite
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  )
}
